import { useCallback } from 'react'
import { DesktopCapturerSourceExt, ExtraVideoOptions } from '../UserContext'
import { desktopMediaSourceToStream, MediaDeviceError } from './MediaDevices'
import useUserContextRef from './useUserContextRef'

export function useDesktopSourceResolver() {
    const { userStateRef } = useUserContextRef()

    const resolveDesktopSourceToMediaStream = useCallback(
        async function (
            id: string,
            desktopCaptureOptions: ExtraVideoOptions,
            fallbackMediaSources?: DesktopCapturerSourceExt[] | null | undefined,
            fallbackLabel?: string
        ): Promise<MediaStream | MediaDeviceError | null> {
            let source = userStateRef.current.mediaSources.desktop.find((s) => s.id === id) || null
            if (!source && fallbackLabel) {
                source = userStateRef.current.mediaSources.desktop.find((s) => s.name === fallbackLabel) || null
            }

            if (source == null && fallbackMediaSources) {
                // search one more time if not found.
                source = fallbackMediaSources.find((s) => s.id === id) || null
            }

            if (source) {
                const screenRecordDesktop: MediaStream | MediaDeviceError | null = await desktopMediaSourceToStream(
                    source,
                    desktopCaptureOptions
                )

                if (screenRecordDesktop instanceof MediaStream) {
                    let _ended = false
                    screenRecordDesktop.getVideoTracks().forEach((vtrack) => {
                        vtrack.addEventListener('ended', () => {
                            if (_ended) {
                                return
                            }
                            _ended = true

                            try {
                                vtrack.stop()
                                screenRecordDesktop.stop()
                            } catch (e) {}
                        })
                    })
                }

                return screenRecordDesktop
            } else {
                return null
            }
        },
        [userStateRef]
    )

    const getDesktopSourceDimensions = useCallback(
        async (id: string, options: ExtraVideoOptions) => {
            const desktopSource = userStateRef.current.mediaSources.desktop.filter((s) => s.id === id)[0] || null
            if (!desktopSource) {
                return null
            }

            const { screenWidth, screenHeight } = desktopSource
            const scaleFactor = desktopSource.scaleFactor || 1

            if (screenWidth && screenHeight) {
                if (options.optionHeight) {
                    const scaledScreenWidth = Math.floor((screenWidth / screenHeight) * options.optionHeight)
                    // calculate scaledScreenHeight while keeping the aspect ratio wrt scaledScreenWidth.
                    const scaledScreenHeight = options.optionHeight
                    return { width: scaledScreenWidth, height: scaledScreenHeight }
                } else {
                    return { width: screenWidth, height: screenHeight }
                }
            } else {
                return null
            }
        },
        [userStateRef]
    )

    return {
        resolveDesktopSourceToMediaStream,
        getDesktopSourceDimensions,
    }
}
